import React, { useEffect, useState, useRef } from 'react';
import { Mail, Loader2, Send, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { toast } from 'sonner';
import { leadsAPI, leadCommunicationAPI } from '../lib/api';

function ShimmerSkeleton() {
  return (
    <div className="flex flex-col flex-1 gap-4 mt-2" data-testid="drafting-skeleton">
      <div>
        <div className="h-3 w-12 rounded bg-muted mb-2" />
        <div className="shimmer-bar h-10 rounded-md w-full" />
      </div>
      <div className="flex-1">
        <div className="h-3 w-14 rounded bg-muted mb-2" />
        <div className="rounded-md border border-blue-100 bg-blue-50/40 p-4 space-y-3 min-h-[180px]">
          <div className="shimmer-bar h-3.5 rounded-full w-[90%]" />
          <div className="shimmer-bar h-3.5 rounded-full w-[75%]" />
          <div className="shimmer-bar h-3.5 rounded-full w-[60%]" />
        </div>
      </div>
      <div className="shimmer-bar h-11 rounded-lg w-full" />
    </div>
  );
}

function TypewriterField({ text, onComplete, speed = 12 }) {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);
  const rafRef = useRef(null);
  const lastTime = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const speedRef = useRef(speed);
  onCompleteRef.current = onComplete;
  speedRef.current = speed;

  useEffect(() => {
    if (!text) return;
    idx.current = 0;
    setDisplayed('');
    lastTime.current = 0;

    const step = (ts) => {
      if (!lastTime.current) lastTime.current = ts;
      if (ts - lastTime.current >= speedRef.current) {
        lastTime.current = ts;
        idx.current++;
        setDisplayed(text.slice(0, idx.current));
        if (idx.current >= text.length) {
          onCompleteRef.current?.();
          return;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [text]);

  return (
    <>
      {displayed}
      {displayed.length < (text?.length || 0) && (
        <span className="inline-block w-0.5 h-4 bg-indigo-500 ml-0.5 animate-pulse align-middle" />
      )}
    </>
  );
}

export default function AIDraftEmailSheet({ isOpen, onClose, leadId, leadEmail, leadName, actionType, actionLabel, onSent }) {
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [typingSubjectDone, setTypingSubjectDone] = useState(false);
  const [typingBodyDone, setTypingBodyDone] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const draftedFor = useRef(null);

  // Draft once per unique sheet open — ignore dependency changes after first call
  useEffect(() => {
    if (!isOpen || !leadId || !actionType) return;

    const key = `${leadId}-${actionType}-${actionLabel}`;
    if (draftedFor.current === key) return;
    draftedFor.current = key;

    let cancelled = false;

    (async () => {
      setDrafting(true);
      setDraft(null);
      setEditing(false);
      setTypingSubjectDone(false);
      setTypingBodyDone(false);

      try {
        const res = await leadsAPI.draftEmail(leadId, {
          action_type: actionType,
          action_label: actionLabel || actionType,
        });
        if (!cancelled) {
          setDraft(res.data);
          setSubject(res.data.subject);
          setBody(res.data.body);
        }
      } catch {
        if (!cancelled) toast.error('Failed to draft email');
      } finally {
        if (!cancelled) setDrafting(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, leadId, actionType, actionLabel]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSending(true);
    try {
      await leadCommunicationAPI.sendEmail(leadId, { subject, message: body });
      toast.success(`Email sent to ${leadEmail}`);
      onSent?.();
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    draftedFor.current = null;
    setDraft(null);
    setDrafting(false);
    setEditing(false);
    setSubject('');
    setBody('');
    onClose();
  };

  const allTypingDone = typingSubjectDone && typingBodyDone;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="sm:max-w-lg flex flex-col h-full" data-testid="ai-draft-email-sheet">
        <SheetHeader className="shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            AI Email Draft
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0 mt-4 gap-4">
          {/* Drafting — shimmer skeleton */}
          {drafting && (
            <div className="flex flex-col flex-1" data-testid="drafting-loader">
              <div className="shrink-0 mb-4">
                <Label className="text-xs text-muted-foreground">To</Label>
                <div className="mt-1 px-3 py-2 rounded-lg border bg-muted/20 text-sm text-foreground/80">
                  {leadName} &lt;{leadEmail}&gt;
                </div>
              </div>

              <ShimmerSkeleton />

              <div className="flex items-center justify-center gap-2 mt-4">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                <p className="text-sm text-muted-foreground" data-testid="drafting-message">
                  AI generating email content...
                </p>
              </div>
            </div>
          )}

          {/* Typewriter preview */}
          {draft && !editing && (
            <>
              <div className="shrink-0">
                <Label className="text-xs text-muted-foreground mb-1.5 block">To</Label>
                <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-sm text-foreground/80" data-testid="draft-to">
                  {draft.to_name} &lt;{draft.to_email}&gt;
                </div>
              </div>

              <div className="shrink-0">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Subject</Label>
                <div className="px-3 py-2.5 rounded-lg border border-border bg-muted/20 text-sm font-medium text-foreground" data-testid="draft-subject">
                  <TypewriterField text={draft.subject} speed={20} onComplete={() => setTypingSubjectDone(true)} />
                </div>
              </div>

              <div className="flex flex-col flex-1 min-h-0">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Message</Label>
                <div
                  className="px-3 py-3 rounded-lg border border-border bg-muted/20 text-sm leading-relaxed flex-1 overflow-y-auto whitespace-pre-wrap text-foreground/90"
                  data-testid="draft-body"
                >
                  {typingSubjectDone ? (
                    <TypewriterField text={draft.body} speed={10} onComplete={() => setTypingBodyDone(true)} />
                  ) : (
                    <span className="inline-block w-0.5 h-4 bg-indigo-500 animate-pulse" />
                  )}
                </div>
              </div>

              {allTypingDone && (
                <div className="flex gap-2 shrink-0" data-testid="draft-actions">
                  <Button variant="outline" className="flex-1" onClick={() => setEditing(true)} data-testid="edit-draft-btn">
                    <Pencil className="h-4 w-4 mr-2" />Edit
                  </Button>
                  <Button className="flex-1" onClick={handleSend} disabled={sending} data-testid="send-draft-btn">
                    {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Send className="h-4 w-4 mr-2" />Send Email</>}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Edit mode */}
          {draft && editing && (
            <>
              <div className="shrink-0">
                <Label className="text-xs text-muted-foreground mb-1.5 block">To</Label>
                <Input value={`${draft.to_name} <${draft.to_email}>`} readOnly className="rounded-lg bg-muted/20 border-border" data-testid="edit-to" />
              </div>
              <div className="shrink-0">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-lg border-border" data-testid="edit-subject" />
              </div>
              <div className="flex flex-col flex-1 min-h-0">
                <Label className="text-xs text-muted-foreground mb-1.5 block">Message</Label>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} className="flex-1 min-h-[200px] resize-none rounded-lg border-border" data-testid="edit-body" />
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}>Preview</Button>
                <Button className="flex-1" onClick={handleSend} disabled={sending} data-testid="send-edited-btn">
                  {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : <><Send className="h-4 w-4 mr-2" />Send Email</>}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
